import React, { useState, useEffect } from 'react';
import './SOCAnalyzer.css';
import jsPDF from 'jspdf';
import AuditorCorner from './AuditorCorner';
import AnalysisDetails from './AnalysisDetails';

const SOCAnalyzer = ({ onBack }) => {
  const [systemFile, setSystemFile] = useState(null);
  const [controlFile, setControlFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [corrections, setCorrections] = useState({});
  const [error, setError] = useState(null);
  const [correctionOptions, setCorrectionOptions] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [tokenUsage, setTokenUsage] = useState(() => {
    // Load from localStorage, or 0 if not present
    const stored = localStorage.getItem('tokenUsage');
    return stored ? parseInt(stored, 10) : 0;
  });
  const [tscControls, setTscControls] = useState([]);
  const [correctionHistory, setCorrectionHistory] = useState([]);
  const [showHistoryDropdown, setShowHistoryDropdown] = useState({});
  const [searchResults, setSearchResults] = useState({});
  const [showAuditorCorner, setShowAuditorCorner] = useState(false);
  const [showAnalysisDetails, setShowAnalysisDetails] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);

  // Midnight reset logic
  useEffect(() => {
    const now = new Date();
    const nextMidnight = new Date(now);
    nextMidnight.setHours(24, 0, 0, 0);
    const msToMidnight = nextMidnight - now;
    const timer = setTimeout(() => {
      setTokenUsage(0);
      localStorage.setItem('tokenUsage', '0');
    }, msToMidnight);
    return () => clearTimeout(timer);
  }, [tokenUsage]);

  // Update localStorage when tokenUsage changes
  useEffect(() => {
    localStorage.setItem('tokenUsage', tokenUsage.toString());
  }, [tokenUsage]);

  // Load correction options
  useEffect(() => {
    const fetchCorrectionOptions = async () => {
      try {
        const response = await fetch('/correction-options');
        if (response.ok) {
          const options = await response.json();
          setCorrectionOptions(options);
        }
      } catch (err) {
        console.error('Failed to load correction options:', err);
      }
    };
    fetchCorrectionOptions();
  }, []);

  // Load TSC_controls.json
  useEffect(() => {
    const fetchTSCControls = async () => {
      try {
        const response = await fetch('/static/TSC_controls.json');
        if (response.ok) {
          const controls = await response.json();
          setTscControls(controls);
        }
      } catch (err) {
        console.error('Failed to load TSC_controls.json:', err);
      }
    };
    fetchTSCControls();
  }, []);

  // Load correction history
  useEffect(() => {
    const fetchCorrectionHistory = async () => {
      try {
        const response = await fetch('/corrections');
        if (response.ok) {
          const history = await response.json();
          setCorrectionHistory(history);
        }
      } catch (err) {
        console.error('Failed to load correction history:', err);
      }
    };
    fetchCorrectionHistory();
  }, []);

  // Generate PDF of the results section using jsPDF.html
  const handleDownloadPDF = () => {
    const input = document.getElementById('resultsSection');
    if (!input) return;
    const pdf = new jsPDF('p', 'pt', 'a4');
    pdf.html(input, {
      callback: (doc) => {
        doc.save('SOC2_Analysis.pdf');
      },
      x: 10,
      y: 10,
      html2canvas: { scale: 0.5 }
    });
  };

  const handleSystemFileChange = (event) => {
    const file = event.target.files[0];
    setSystemFile(file);
    setError(null);
  };

  const handleControlFileChange = (event) => {
    const file = event.target.files[0];
    setControlFile(file);
    setError(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!systemFile || !controlFile) {
      setError('Please select both system description and control list files.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setResults(null);
    setJobId(null);
    setJobStatus(null);

    const formData = new FormData();
    formData.append('system_description', systemFile);
    formData.append('control_list', controlFile);

    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await fetch('/compare-documents', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to process documents');
      }

      const data = await response.json();
      
      // Check if we got immediate results (legacy mode) or job ID (new mode)
      if (data.results) {
        // Legacy mode - immediate results
      setResults(data);
      if (typeof data.total_token_usage === 'number') {
        setTokenUsage(prev => prev + data.total_token_usage);
        }
        setIsLoading(false);
      } else if (data.job_id) {
        // New mode - job queued for processing
        setJobId(data.job_id);
        setJobStatus('queued');
        startPollingForResults(data.job_id);
      } else {
        throw new Error('Unexpected response format');
      }
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
    }
  };

  const startPollingForResults = (jobId) => {
    /**
     * Real-time job status updates using Server-Sent Events (SSE)
     * Improved with retry logic and better error handling
     * Handles timeout (600 seconds) and provides user feedback
     */
    let retryCount = 0;
    const maxRetries = 3;
    let retryTimeout = null;
    
    const connectSSE = () => {
      try {
        // Use the current protocol to avoid HTTP->HTTPS redirect issues with EventSource
        const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
        const host = window.location.host;
        const eventSource = new EventSource(`${protocol}//${host}/job-stream/${jobId}?user_id=web_user`);
        
        eventSource.onopen = (event) => {
          console.log('SSE connection opened');
          retryCount = 0; // Reset retry count on successful connection
        };
        
        eventSource.onmessage = (event) => {
      try {
            const data = JSON.parse(event.data);
            
            // Handle different message types
            if (data.error) {
              setError(`Error: ${data.error}`);
              eventSource.close();
              setIsLoading(false);
              return;
            }
            
            // Handle timeout
            if (data.status === 'timeout') {
              setError(data.message || 'Analysis failed please try again or reduce the document size.');
              eventSource.close();
              setIsLoading(false);
              return;
            }
            
            // Update job status
            setJobStatus(data.status);
            
            // Handle completion
            if (data.status === 'completed' && data.result) {
              // Extract the actual result data (reusing existing logic)
              const actualResults = data.result;
              
              // Validate results have actual content before displaying  
              if (!actualResults || !actualResults.results || actualResults.results.length === 0) {
                setError('Analysis completed but no results found. Please try again.');
                eventSource.close();
                setIsLoading(false);
                return;
              }
              
              // Check for "Model not available" errors or empty analysis
              const validResults = actualResults.results.filter(result => {
                const analysis = result.analysis;
                return analysis && 
                       analysis.match_status && 
                       analysis.match_status !== 'Unknown' &&
                       analysis.explanation &&
                       analysis.explanation.trim() !== '';
              });
              
              if (validResults.length === 0) {
                setError('Analysis completed but all results show "Model not available". Please check server logs.');
                eventSource.close();
                setIsLoading(false);
                return;
              }
              
              // Show warning if some results are invalid but others are good
              if (validResults.length < actualResults.results.length) {
                console.warn(`${actualResults.results.length - validResults.length} out of ${actualResults.results.length} results had errors`);
              }
              
              setResults(actualResults);
              
              if (typeof actualResults.total_token_usage === 'number') {
                setTokenUsage(prev => prev + actualResults.total_token_usage);
              }
              
              eventSource.close();
              setIsLoading(false);
            } else if (data.status === 'failed') {
              setError(data.message || 'Job processing failed. Please try again.');
              eventSource.close();
              setIsLoading(false);
            }
            
          } catch (parseError) {
            console.error('Error parsing SSE data:', parseError);
          }
        };
        
        eventSource.onerror = (error) => {
          console.error('SSE connection error:', error);
          eventSource.close();
          
          // Retry logic - only retry for connection errors, not server errors
          if (retryCount < maxRetries) {
            retryCount++;
            console.log(`Retrying SSE connection (${retryCount}/${maxRetries}) in 2 seconds...`);
            
            retryTimeout = setTimeout(() => {
              connectSSE();
            }, 2000);
            
            // Store timeout for cleanup
            window.sseRetryTimeout = retryTimeout;
          } else {
            // After all retries failed, fall back to polling
            console.log('SSE failed, falling back to polling...');
            setError('Connection unstable, switching to polling mode...');
            
            // Start polling fallback
            const pollInterval = setInterval(async () => {
              try {
                const sessionToken = localStorage.getItem('session_token');
                const statusResponse = await fetch(`/job-status/${jobId}`, {
                  headers: {
                    'Authorization': `Bearer ${sessionToken}`
                  }
                });
                if (statusResponse.ok) {
        const statusData = await statusResponse.json();
        setJobStatus(statusData.status);
        
        if (statusData.status === 'completed') {
          const resultResponse = await fetch(`/job-result/${jobId}`, {
            headers: {
              'Authorization': `Bearer ${sessionToken}`
            }
          });
          if (resultResponse.ok) {
            const resultData = await resultResponse.json();
                      if (resultData.result) {
                        setResults(resultData.result);
          setIsLoading(false);
                        clearInterval(pollInterval);
                      }
                    }
        } else if (statusData.status === 'failed') {
          setError('Job processing failed. Please try again.');
          setIsLoading(false);
                    clearInterval(pollInterval);
                  }
                }
              } catch (pollError) {
                console.error('Polling error:', pollError);
              }
            }, 5000);
            
            // Store polling interval for cleanup
            setPollingInterval(pollInterval);
          }
        };
        
        // Store EventSource for cleanup
        setPollingInterval(eventSource);
        
      } catch (err) {
        console.error('Failed to start SSE connection:', err);
        setError('Failed to connect to server. Please try again.');
        setIsLoading(false);
      }
    };
    
    // Start the connection
    connectSSE();
  };

  // Cleanup SSE connection or polling interval on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        // Handle both EventSource (SSE) and setInterval cleanup
        if (typeof pollingInterval.close === 'function') {
          pollingInterval.close(); // EventSource cleanup
        } else if (typeof pollingInterval === 'number') {
          clearInterval(pollingInterval); // Legacy interval cleanup
        }
      }
      
      // Clear any pending retry timeouts
      if (window.sseRetryTimeout) {
        clearTimeout(window.sseRetryTimeout);
      }
    };
  }, [pollingInterval]);

  const handleCorrectClick = (index) => {
    const existing = corrections[index] || {};
    const result = results.results[index];
    setCorrections({
      ...corrections,
      [index]: {
        show: true,
        // System-generated fields (read-only)
        control_id: existing.control_id || '',
        control_text: existing.control_text || '',
        internal_control_id: result.control_text?.control_id || '',
        control_text_display: result.control_text?.control_text || '',
        system_description: result.analysis?.quote || result.matched_paragraph?.text || '',
        ai_reasoning: result.analysis?.explanation || '',
        tsc_category: existing.tsc_category || 'General',
        
        // Auditor-required fields
        compliance_status: existing.compliance_status || 'non_compliant',
        failure_reason: existing.failure_reason || 'missing_documentation',
        audit_reasoning: existing.audit_reasoning || '',
        correction_requirements: existing.correction_requirements || '',
        
        // Optional fields
        confidence_level: existing.confidence_level || 5
      }
    });
  };

  // Helper to get control_text (objective) from TSC_controls.json by control_id
  const getControlTextById = (controlId) => {
    const ctrl = tscControls.find(ctrl => ctrl.control_id === controlId);
    return ctrl ? ctrl.control_objective : '';
  };

  // Helper to get tsc_category (ID + description) from TSC_controls.json by control_id
  const getTSCCategoryById = (controlId) => {
    const ctrl = tscControls.find(ctrl => ctrl.control_id === controlId);
    return ctrl ? `${ctrl.control_id} – ${ctrl.control_objective}` : '';
  };

  const handleCorrectionChange = (index, field, value) => {
    setCorrections(prev => ({
      ...prev,
      [index]: {
        ...prev[index],
        [field]: value
      }
    }));
  };

  const handleCorrectionSubmit = async (index) => {
    const corr = corrections[index];
    const result = results.results[index];
    
    const payload = {
      // Link to current analysis
      job_id: jobId,
      
      // System-generated fields (match backend expected structure)
      control_id: corr.control_id,
      control_text: getControlTextById(corr.control_id),
      system_description: corr.system_description,
      ai_original_decision: result.analysis?.match_status,
      ai_original_reasoning: result.analysis?.explanation,
      ai_system_quote: result.analysis?.quote || result.matched_paragraph?.text,
      
      // Auditor-required fields
      compliance_status: corr.compliance_status,
      audit_reasoning: corr.audit_reasoning,
      system_focus_area: corr.system_focus_area,
      
      // Structured correction requirements (ensure it's an object)
      correction_requirements: typeof corr.correction_requirements === 'object' ? 
        corr.correction_requirements : 
        {
          immediate: corr.correction_requirements || '',
          ongoing: '',
          evidence_needed: ''
        },
      
      // Optional fields
      confidence_level: corr.confidence_level
    };
    
    try {
      const resp = await fetch('/corrections', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (!resp.ok) {
        const errorData = await resp.json();
        throw new Error(errorData.detail || 'Failed to save correction');
      }
      
      const responseData = await resp.json();
      console.log('Correction saved successfully:', responseData);
      
      // Update UI with correction
      setResults(prev => {
        const newResults = [...prev.results];
        newResults[index] = {
          ...newResults[index],
          matched_paragraph: { text: corr.system_description, section: newResults[index].matched_paragraph?.section || '' },
          alignment_analysis: {
            ...newResults[index].alignment_analysis,
            match_status: corr.compliance_status,
            explanation: corr.audit_reasoning || newResults[index].alignment_analysis.explanation,
            correction_applied: true,
            correction_requirements: corr.correction_requirements
          }
        };
        return { ...prev, results: newResults };
      });
      
      // Hide form
      setCorrections(prev => ({ ...prev, [index]: { ...prev[index], show: false } }));
      
      // Refresh correction history
      try {
        const historyResponse = await fetch('/corrections');
        if (historyResponse.ok) {
          const history = await historyResponse.json();
          setCorrectionHistory(history);
        }
      } catch (historyErr) {
        console.error('Failed to refresh correction history:', historyErr);
      }
      
    } catch (err) {
      console.error('Error saving correction:', err);
      setError(err.message);
    }
  };

  // Validation: Only allow save if all required fields are filled
  const isCorrectionValid = (correction) => {
    return (
      correction.control_id &&
      correction.compliance_status &&
      correction.audit_reasoning &&
      correction.system_focus_area &&
      correction.confidence_level
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Fully aligned':
        return '#28a745'; // Green
      case 'Partially aligned':
        return '#ffc107'; // Yellow
      case 'Not aligned':
        return '#dc3545'; // Red
      case 'No match found':
        return '#6c757d'; // Gray
      case 'Error':
        return '#dc3545'; // Red
      default:
        return '#6c757d'; // Gray
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Fully aligned':
        return '✅';
      case 'Partially aligned':
        return '⚠️';
      case 'Not aligned':
        return '❌';
      case 'No match found':
        return '❓';
      case 'Error':
        return '⚠️';
      default:
        return '❓';
    }
  };

  const getLoadingMessage = () => {
    if (jobStatus === 'queued') {
      return 'Job Queued - Waiting for Processing...';
    } else if (jobStatus === 'processing') {
      return 'Processing on GPU Server...';
    } else {
      return 'Analyzing Documents...';
    }
  };

  const getJobStatusIcon = (status) => {
    switch (status) {
      case 'queued':
        return '⏳';
      case 'processing':
        return '🔄';
      case 'completed':
        return '✅';
      case 'failed':
        return '❌';
      default:
        return '❓';
    }
  };

  const getJobStatusText = (status) => {
    switch (status) {
      case 'queued': return 'Queued for processing...';
      case 'processing': return 'Processing documents...';
      case 'completed': return 'Analysis completed';
      case 'failed': return 'Analysis failed';
      case 'timeout': return 'Analysis timed out';
      default: return 'Unknown status';
    }
  };

  // RAG status helper functions
  const getRagStatusIcon = (ragStatus) => {
    switch (ragStatus) {
      case 'enhanced': return '🧠';
      case 'model_only': return '⚡';
      case 'error': return '⚠️';
      default: return '❓';
    }
  };

  const getRagStatusColor = (ragStatus) => {
    switch (ragStatus) {
      case 'enhanced': return '#28a745';
      case 'model_only': return '#ffc107';
      case 'error': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getRagStatusText = (ragStatus) => {
    switch (ragStatus) {
      case 'enhanced': return 'Enhanced RAG';
      case 'model_only': return 'Model Only';
      case 'error': return 'Error';
      default: return 'Unknown';
    }
  };

  const getCorrectionHistoryForControl = (controlId) => {
    if (!controlId) return [];
    return correctionHistory
      .filter(correction => correction.control_id === controlId)
      .slice(-5); // Last 5 corrections
  };

  const searchCorrections = (query, controlId) => {
    if (!query || query.length < 2) return [];
    
    const queryLower = query.toLowerCase();
    const results = correctionHistory
      .filter(correction => {
        // First priority: exact control_id match
        if (controlId && correction.control_id === controlId) return true;
        
        // Second priority: text similarity in reasoning
        const reasonLower = correction.reason?.toLowerCase() || '';
        const controlTextLower = correction.control_text?.toLowerCase() || '';
        
        return reasonLower.includes(queryLower) || 
               controlTextLower.includes(queryLower);
      })
      .map(correction => ({
        ...correction,
        relevance: calculateRelevance(queryLower, correction, controlId)
      }))
      .sort((a, b) => b.relevance - a.relevance)
      .slice(0, 5); // Top 5 results
    
    return results;
  };

  const calculateRelevance = (query, correction, controlId) => {
    let score = 0;
    
    // Exact control_id match gets highest priority
    if (controlId && correction.control_id === controlId) {
      score += 100;
    }
    
    // Text similarity in reasoning
    const reason = correction.reason?.toLowerCase() || '';
    if (reason.includes(query)) {
      score += 50;
      // Bonus for exact phrase match
      if (reason.includes(query)) score += 25;
    }
    
    // Text similarity in control text
    const controlText = correction.control_text?.toLowerCase() || '';
    if (controlText.includes(query)) {
      score += 30;
    }
    
    return score;
  };

  const handleAuditReasoningChange = (index, value) => {
    handleCorrectionChange(index, 'audit_reasoning', value);
    
    // Search for relevant corrections when user types
    if (value.length >= 2) {
      const controlId = corrections[index]?.control_id;
      const results = searchCorrections(value, controlId);
      setSearchResults(prev => ({ ...prev, [index]: results }));
      setShowHistoryDropdown(prev => ({ ...prev, [index]: results.length > 0 }));
    } else {
      setSearchResults(prev => ({ ...prev, [index]: [] }));
      setShowHistoryDropdown(prev => ({ ...prev, [index]: false }));
    }
  };

  const selectHistoryItem = (index, historyItem) => {
    handleCorrectionChange(index, 'audit_reasoning', historyItem.reason);
    setShowHistoryDropdown(prev => ({ ...prev, [index]: false }));
    setSearchResults(prev => ({ ...prev, [index]: [] }));
  };

  // Generate Recommendations Section
  const generateRecommendations = () => {
    // Implementation of generateRecommendations function
  };

  // Helper to check if there are not aligned controls
  const hasNotAlignedControls = () => {
    // Implementation of hasNotAlignedControls function
  };

  // Auditor's Corner handlers
  const handleViewAnalysis = (jobId) => {
    setSelectedJobId(jobId);
    setShowAuditorCorner(false);
    setShowAnalysisDetails(true);
  };

  const handleBackToAnalyses = () => {
    setShowAnalysisDetails(false);
    setShowAuditorCorner(true);
  };

  const handleCloseAuditorCorner = () => {
    setShowAuditorCorner(false);
    setShowAnalysisDetails(false);
    setSelectedJobId(null);
  };

  return (
    <div className="soc-analyzer">
      <header className="soc-header">
        <div className="header-content">
          <button 
            className="back-button"
            onClick={onBack}
            aria-label="Back to features"
          >
            ← Back to Features
          </button>
          <h1 className="soc-title">SOC 2 Analyzer</h1>
          <p className="soc-subtitle">AI-powered compliance analysis and document comparison</p>
          <button 
            className="auditor-corner-button"
            onClick={() => setShowAuditorCorner(true)}
            aria-label="Open Auditor's Corner"
          >
            🔍 Auditor's Corner
          </button>
        </div>
      </header>

      <main className="soc-main">
        <div className="upload-section">
          <div className="upload-container">
            <h2>Document Upload</h2>
            <form onSubmit={handleSubmit} className="upload-form">
              <div className="file-input-group">
                <div className="file-input-wrapper">
                  <label htmlFor="system-file" className="file-label">
                    <span className="file-icon">📄</span>
                    System Description
                  </label>
                  <input
                    id="system-file"
                    type="file"
                    accept=".docx,.pdf,.xlsx"
                    onChange={handleSystemFileChange}
                    className="file-input"
                  />
                  {systemFile && (
                    <div className="file-selected">
                      ✓ {systemFile.name}
                    </div>
                  )}
                </div>

                <div className="file-input-wrapper">
                  <label htmlFor="control-file" className="file-label">
                    <span className="file-icon">📋</span>
                    Control List
                  </label>
                  <input
                    id="control-file"
                    type="file"
                    accept=".docx,.pdf,.xlsx"
                    onChange={handleControlFileChange}
                    className="file-input"
                  />
                  {controlFile && (
                    <div className="file-selected">
                      ✓ {controlFile.name}
                    </div>
                  )}
                </div>
              </div>

              <button
                type="submit"
                disabled={!systemFile || !controlFile || isLoading}
                className="analyze-button"
              >
                {isLoading ? (
                  <>
                    <span className="spinner"></span>
                    {getLoadingMessage()}
                  </>
                ) : (
                  <>
                    <span className="analyze-icon">🔍</span>
                    Analyze Documents
                  </>
                )}
              </button>
              
              {jobId && jobStatus && (
                <div className="job-status">
                  <div className="status-info">
                    <span className="job-id">Job ID: {jobId.substring(0, 8)}...</span>
                    <span className="job-status-badge">{getJobStatusIcon(jobStatus)} {getJobStatusText(jobStatus)}</span>
                  </div>
                  {(jobStatus === 'processing' || jobStatus === 'queued') && (
                    <div className="processing-details">
                      <p>🔄 Analysis in progress, please wait. Large document analysis requires more time (~5 minutes). Results will be displayed as soon as processing is completed.</p>
                      {jobStatus === 'processing' && <p>⚡ Currently processing on GPU server...</p>}
                      {jobStatus === 'queued' && <p>⏳ Waiting in queue for processing...</p>}
                    </div>
                  )}
                </div>
              )}
            </form>

            {error && (
              <div className="error-message">
                <span className="error-icon">⚠️</span>
                {error}
              </div>
            )}
          </div>
        </div>

        {results && (
          <div className="results-section" id="resultsSection">
            <div className="results-header">
              <h2>Analysis Results</h2>
              <div className="results-actions">
                <button onClick={handleDownloadPDF} className="download-button">
                  📄 Download PDF
                </button>
              </div>
            </div>

            <div className="results-summary">
              <div className="summary-card">
                <h3>Summary</h3>
                <div className="summary-stats">
                  <div className="stat">
                    <span className="stat-value">{results.total_controls || results.results?.length || 0}</span>
                    <span className="stat-label">Total Controls</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">{results.results?.filter(r => r.analysis?.match_status === 'Fully aligned').length || 0}</span>
                    <span className="stat-label">Fully Aligned</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">{results.results?.filter(r => r.analysis?.match_status === 'Partially aligned').length || 0}</span>
                    <span className="stat-label">Partially Aligned</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">{results.results?.filter(r => r.analysis?.match_status === 'Not aligned').length || 0}</span>
                    <span className="stat-label">Not Aligned</span>
                  </div>
                </div>
              </div>

              {/* RAG Status Summary */}
              {(results.rag_status_summary || results.rag_enhanced_count !== undefined) && (
                <div className="summary-card rag-summary">
                  <h3>🧠 Analysis Method</h3>
                  <div className="rag-message">
                    {results.rag_analysis_message || 
                     results.ui_display?.rag_effectiveness_display || 
                     'Analysis completed'}
                  </div>
                  <div className="summary-stats">
                    <div className="stat rag-stat">
                      <span className="stat-value" style={{ color: getRagStatusColor('enhanced') }}>
                        {results.rag_status_summary?.enhanced || results.rag_enhanced_count || 0}
                      </span>
                      <span className="stat-label">
                        {getRagStatusIcon('enhanced')} Enhanced RAG
                      </span>
                    </div>
                    <div className="stat rag-stat">
                      <span className="stat-value" style={{ color: getRagStatusColor('model_only') }}>
                        {results.rag_status_summary?.model_only || results.rag_model_only_count || 0}
                      </span>
                      <span className="stat-label">
                        {getRagStatusIcon('model_only')} Model Only
                      </span>
                    </div>
                    {(results.rag_status_summary?.errors || results.rag_error_count) > 0 && (
                      <div className="stat rag-stat">
                        <span className="stat-value" style={{ color: getRagStatusColor('error') }}>
                          {results.rag_status_summary?.errors || results.rag_error_count || 0}
                        </span>
                        <span className="stat-label">
                          {getRagStatusIcon('error')} Errors
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* RAG Performance Metrics */}
                  {results.rag_metrics && (
                    <div className="rag-metrics">
                      <div className="metric-row">
                        <span className="metric-label">📊 RAG Effectiveness:</span>
                        <span className="metric-value">{results.rag_metrics.effectiveness_percentage?.toFixed(1)}%</span>
                      </div>
                      {results.rag_metrics.total_audit_examples_used > 0 && (
                        <div className="metric-row">
                          <span className="metric-label">📄 Audit Examples Used:</span>
                          <span className="metric-value">{results.rag_metrics.total_audit_examples_used}</span>
                        </div>
                      )}
                      {results.performance_metrics && (
                        <div className="metric-row">
                          <span className="metric-label">⚡ Processing Speed:</span>
                          <span className="metric-value">{results.ui_display?.performance_display || `${results.performance_metrics.controls_per_second?.toFixed(1)} controls/sec`}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="results-list">
              {results.results?.map((result, index) => (
                <div key={index} className="result-item">
                  <div className="result-header">
                    <div className="control-info">
                      <span className="control-id">
                        {result.control_text?.control_id || `Control ${index + 1}`}
                      </span>
                      <div className="status-badges">
                      <div 
                        className="status-badge"
                        style={{ 
                            backgroundColor: getStatusColor(result.analysis?.match_status),
                          color: 'white'
                        }}
                      >
                          {getStatusIcon(result.analysis?.match_status)}
                          {result.analysis?.match_status || 'Unknown'}
                        </div>
                        {/* RAG Status Badge */}
                        {result.rag_status && (
                          <div 
                            className="rag-badge"
                            style={{ 
                              backgroundColor: getRagStatusColor(result.rag_status),
                              color: 'white'
                            }}
                            title={`Analysis method: ${getRagStatusText(result.rag_status)}`}
                          >
                            {getRagStatusIcon(result.rag_status)}
                            {getRagStatusText(result.rag_status)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="result-content">
                    <div className="control-statement">
                      <h4>Control Statement</h4>
                      <p>{result.control_text?.control_text}</p>
                    </div>

                    {/* Enhanced matched paragraph section - always visible and more prominent */}
                    <div className="matched-paragraph-section">
                      <h4>📄 Analyzed System Text</h4>
                      {result.analysis?.quote ? (
                        <div className="matched-content">
                          <div className="paragraph-text">
                            "{result.analysis.quote}"
                          </div>
                          {result.matched_paragraph?.section && (
                            <div className="section-info">
                              <strong>Source Section:</strong> {result.matched_paragraph.section}
                            </div>
                          )}
                          {result.similarity_score && (
                            <div className="confidence-info">
                              <strong>Match Confidence:</strong> {(result.similarity_score * 100).toFixed(1)}%
                            </div>
                          )}
                          <div className="analysis-context">
                            <small>💡 This is the specific text from your system description that the AI analyzed against the control requirement.</small>
                          </div>
                        </div>
                      ) : (
                        <div className="no-match-content">
                          <p className="no-match-text">❌ No specific system text was matched for this control</p>
                          <p className="no-match-explanation">
                            This control may require implementation that is not currently documented in the system description.
                          </p>
                      </div>
                    )}
                    </div>

                    <div className="analysis-explanation">
                      <h4>🤖 AI Analysis & Reasoning</h4>
                      <p>{result.analysis?.explanation || 'No explanation available'}</p>
                      
                      {/* RAG Enhancement Information */}
                      {result.rag_status && result.metadata && (
                        <div className="rag-info">
                          <div className="rag-method">
                            <strong>Analysis Method:</strong> {getRagStatusIcon(result.rag_status)} {getRagStatusText(result.rag_status)}
                            {result.metadata.audit_examples_used > 0 && (
                              <span className="audit-examples"> ({result.metadata.audit_examples_used} audit examples used)</span>
                            )}
                          </div>
                          
                          {result.metadata.confidence_indicators && (
                            <div className="confidence-indicators">
                              <strong>Confidence Indicators:</strong>
                              {result.metadata.confidence_indicators.has_quote && (
                                <span className="confidence-item">✅ System text matched</span>
                              )}
                              {result.metadata.confidence_indicators.rag_context_available && (
                                <span className="confidence-item">🧠 RAG context available</span>
                              )}
                              {result.metadata.confidence_indicators.audit_examples_available && (
                                <span className="confidence-item">📄 Audit examples available</span>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="result-actions">
                      <button 
                        onClick={() => handleCorrectClick(index)}
                        className="correct-button"
                      >
                        ✏️ Make Correction
                      </button>
                    </div>

                    {corrections[index]?.show && (
                      <div className="correction-form">
                        <div className="form-group">
                          <label>TSC Control ID *</label>
                          <select
                            value={corrections[index]?.control_id || ''}
                            onChange={(e) => handleCorrectionChange(index, 'control_id', e.target.value)}
                            required
                          >
                            <option value="">Select Control ID</option>
                            {tscControls.map(control => (
                              <option key={control.control_id} value={control.control_id}>
                                {control.control_id} - {control.control_objective}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="form-group">
                          <label>AI Original Decision</label>
                          <input
                            type="text"
                            value={result.analysis?.match_status || 'Unknown'} 
                            readOnly 
                            className="readonly-field"
                          />
                        </div>

                        <div className="form-group">
                          <label>AI Original Reasoning</label>
                          <textarea
                            value={result.analysis?.explanation || 'No explanation provided'} 
                            readOnly 
                            className="readonly-field"
                            rows={2}
                          />
                        </div>

                        <div className="form-group">
                          <label>Auditor Corrected Decision *</label>
                          <select
                            value={corrections[index]?.compliance_status || ''}
                            onChange={(e) => handleCorrectionChange(index, 'compliance_status', e.target.value)}
                            required
                          >
                            <option value="">Select Status</option>
                            <option value="Fully aligned">Fully aligned</option>
                            <option value="Partially aligned">Partially aligned</option>
                            <option value="Not aligned">Not aligned</option>
                          </select>
                        </div>

                        <div className="form-group">
                          <label>Audit Reasoning *</label>
                          <div className="reasoning-input-container">
                          <textarea
                            value={corrections[index]?.audit_reasoning || ''}
                              onChange={(e) => handleAuditReasoningChange(index, e.target.value)}
                            rows={3}
                              placeholder="Why do you disagree with AI's analysis?"
                            required
                          />
                            {showHistoryDropdown[index] && searchResults[index]?.length > 0 && (
                              <div className="search-results">
                                <div className="search-header">
                                  💡 Related corrections ({searchResults[index].length} found):
                                </div>
                                {searchResults[index].map((item, idx) => (
                                  <div key={idx} className="search-item" onClick={() => selectHistoryItem(index, item)}>
                                    <div className="search-reason">{item.reason}</div>
                                    <div className="search-meta">Control: {item.control_id} | Status: {item.match_label}</div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="form-group">
                          <label>System Focus Area *</label>
                          <textarea
                            value={corrections[index]?.system_focus_area || ''}
                            onChange={(e) => handleCorrectionChange(index, 'system_focus_area', e.target.value)}
                            rows={2}
                            placeholder="Which part of system description did you focus on?"
                            required
                          />
                        </div>

                        <div className="correction-requirements">
                          <label>Correction Requirements (for recommendations)</label>
                          
                          <div className="form-group">
                            <label>Immediate Actions (0-30 days)</label>
                            <textarea
                              value={corrections[index]?.correction_requirements?.immediate || ''}
                              onChange={(e) => handleCorrectionChange(index, 'correction_requirements', {
                                ...corrections[index]?.correction_requirements,
                                immediate: e.target.value
                              })}
                              rows={2}
                              placeholder="Actions that can be done immediately..."
                            />
                          </div>

                          <div className="form-group">
                            <label>Ongoing Processes (30-90 days)</label>
                            <textarea
                              value={corrections[index]?.correction_requirements?.ongoing || ''}
                              onChange={(e) => handleCorrectionChange(index, 'correction_requirements', {
                                ...corrections[index]?.correction_requirements,
                                ongoing: e.target.value
                              })}
                              rows={2}
                              placeholder="Systematic processes to establish..."
                            />
                          </div>

                          <div className="form-group">
                            <label>Evidence Required</label>
                            <textarea
                              value={corrections[index]?.correction_requirements?.evidence_needed || ''}
                              onChange={(e) => handleCorrectionChange(index, 'correction_requirements', {
                                ...corrections[index]?.correction_requirements,
                                evidence_needed: e.target.value
                              })}
                              rows={2}
                              placeholder="Specific documentation auditors expect..."
                            />
                          </div>
                        </div>

                        <div className="form-group">
                          <label>Confidence Level: {corrections[index]?.confidence_level || 5}</label>
                          <input
                            type="range"
                            min="1"
                            max="10"
                            value={corrections[index]?.confidence_level || 5}
                            onChange={(e) => handleCorrectionChange(index, 'confidence_level', parseInt(e.target.value))}
                            className="confidence-slider"
                          />
                          <div className="confidence-labels">
                            <span>Low (1)</span>
                            <span>High (10)</span>
                          </div>
                        </div>

                        <button 
                          type="button" 
                          className="submit-correction"
                          onClick={() => handleCorrectionSubmit(index)}
                          disabled={!isCorrectionValid(corrections[index])}
                        >
                          Save Correction
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Generate Recommendations Section */}
            {results && (
              <div className="recommendation-section">
                <button 
                  className="generate-recommendations" 
                  onClick={generateRecommendations}
                  disabled={!hasNotAlignedControls()}
                >
                  📋 Generate Recommendations
                </button>
                <p className="note">Generate recommendations for not aligned controls</p>
                
                {results.recommendations && (
                  <div className="recommendations-output">
                    <h3>📋 Improvement Recommendations</h3>
                    {results.recommendations.map((rec, index) => (
                      <div key={index} className="recommendation">
                        <h4>Control: {rec.control_id}</h4>
                        <div className="immediate">
                          <strong>Immediate (0-30 days):</strong>
                          <ul>{rec.immediate?.map((item, idx) => <li key={idx}>{item}</li>)}</ul>
                        </div>
                        <div className="ongoing">
                          <strong>Ongoing (30-90 days):</strong>
                          <ul>{rec.ongoing?.map((item, idx) => <li key={idx}>{item}</li>)}</ul>
                        </div>
                        <div className="evidence">
                          <strong>Evidence Required:</strong>
                          <ul>{rec.evidence_needed?.map((item, idx) => <li key={idx}>{item}</li>)}</ul>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Auditor's Corner Modal */}
      {showAuditorCorner && (
        <AuditorCorner 
          onClose={handleCloseAuditorCorner}
          onViewAnalysis={handleViewAnalysis}
        />
      )}

      {/* Analysis Details Modal */}
      {showAnalysisDetails && selectedJobId && (
        <AnalysisDetails 
          jobId={selectedJobId}
          onClose={handleCloseAuditorCorner}
          onBack={handleBackToAnalyses}
        />
      )}
    </div>
  );
};

export default SOCAnalyzer; 